'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { AlertCircle, Info } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { useRTL } from '@/hooks/useRTL';

gsap.registerPlugin(ScrollTrigger);

export default function PermanenceSection() {
    const { t } = useTranslation('dental_veneers');
    const { isRTL } = useRTL();
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Set initial states
            gsap.set(titleRef.current, { opacity: 0, y: 50 });
            gsap.set(contentRef.current, { opacity: 0, y: 30 });

            // Title animation with scroll trigger
            gsap.to(titleRef.current, {
                opacity: 1,
                y: 0,
                duration: 1,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: titleRef.current,
                    start: "top 80%",
                    end: "bottom 20%",
                    toggleActions: "play none none reverse",
                    scrub: false
                }
            });

            // Content animation
            gsap.to(contentRef.current, {
                opacity: 1,
                y: 0,
                duration: 0.8,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: contentRef.current,
                    start: "top 75%",
                    end: "bottom 25%",
                    toggleActions: "play none none reverse",
                    scrub: false
                }
            });

            // Emphasis animation on the word "permanently"
            const permanentlyElement = contentRef.current?.querySelector('.permanently-emphasis');
            if (permanentlyElement) {
                gsap.set(permanentlyElement, { scale: 1 });
                gsap.to(permanentlyElement, {
                    scale: 1.05,
                    duration: 0.3,
                    yoyo: true,
                    repeat: 1,
                    ease: "power2.inOut",
                    scrollTrigger: {
                        trigger: permanentlyElement,
                        start: "top 80%",
                        end: "bottom 20%",
                        toggleActions: "play none none reverse",
                        scrub: false
                    }
                });
            }

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            ref={sectionRef}
            className="py-20 bg-white dark:bg-slate-800 relative overflow-hidden"
        >
            {/* Background decoration */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute top-20 right-20 w-64 h-64 bg-gold rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 left-20 w-80 h-80 bg-gold rounded-full blur-2xl"></div>
            </div>

            <div className="container relative">
                {/* Section Header */}
                <div className="text-center max-w-4xl mx-auto mb-16">
                    <h2
                        ref={titleRef}
                        className={cn(
                            "text-4xl md:text-5xl lg:text-6xl font-bold text-navy dark:text-white mb-8 font-serif",
                            isRTL && "font-arabic"
                        )}
                    >
                        {t('permanence.title')}
                    </h2>
                </div>

                {/* Content Block */}
                <div ref={contentRef} className="max-w-4xl mx-auto">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 md:p-12 shadow-lg border border-slate-200 dark:border-slate-700 relative overflow-hidden">
                        {/* Background Visual */}
                        <div className="absolute top-0 right-0 w-32 h-32 bg-gold/10 rounded-full blur-2xl"></div>

                        <div className="relative z-10">
                            {/* Icon */}
                            <div className="flex items-center justify-center mb-8">
                                <div className="w-20 h-20 bg-gold rounded-full flex items-center justify-center">
                                    <Info className="w-10 h-10 text-white" />
                                </div>
                            </div>

                            {/* Main Content */}
                            <div className="text-center space-y-6">
                                <p className={cn(
                                    "text-xl md:text-2xl text-slate-600 dark:text-slate-300 leading-relaxed",
                                    isRTL && "font-arabic"
                                )}>
                                    {t('permanence.description')}
                                </p>
                                <p className={cn(
                                    "text-xl text-slate-600 dark:text-slate-300 leading-relaxed",
                                    isRTL && "font-arabic"
                                )}>
                                    {t('permanence.commitment')}
                                </p>
                                <div className="bg-gold/10 rounded-xl p-6 border border-gold/20">
                                    <div className="relative">
                                        <div className={cn(
                                            "w-6 h-6 text-gold absolute mt-1",
                                            isRTL ? "right-0" : "left-0"
                                        )}>
                                            <AlertCircle className="w-6 h-6" />
                                        </div>
                                        <div className={cn(
                                            "w-full",
                                            isRTL ? "pr-10 text-right" : "pl-10 text-left"
                                        )}>
                                            <h4 className={cn(
                                                "text-lg font-bold text-navy dark:text-white mb-2",
                                                isRTL && "font-arabic"
                                            )}>
                                                {t('permanence.consideration.title')}
                                            </h4>
                                            <p className={cn(
                                                "text-slate-600 dark:text-slate-300 leading-relaxed",
                                                isRTL && "font-arabic"
                                            )}>
                                                {t('permanence.consideration.description')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Visual Elements */}
                            <div className="grid md:grid-cols-2 gap-8 mt-12">
                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.6, delay: 0.2 }}
                                    viewport={{ once: true }}
                                    className="text-center"
                                >
                                    <div className="w-16 h-16 bg-gold rounded-full flex items-center justify-center mx-auto mb-4">
                                        <span className="text-2xl">🦷</span>
                                    </div>
                                    <h4 className={cn(
                                        "text-lg font-bold text-navy dark:text-white mb-2",
                                        isRTL && "font-arabic"
                                    )}>{t('permanence.visual.before.label')}</h4>
                                    <p className={cn(
                                        "text-slate-600 dark:text-slate-300 text-sm",
                                        isRTL && "font-arabic"
                                    )}>
                                        {t('permanence.visual.before.description')}
                                    </p>
                                </motion.div>

                                <motion.div
                                    initial={{ opacity: 0, x: 20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.6, delay: 0.4 }}
                                    viewport={{ once: true }}
                                    className="text-center"
                                >
                                    <div className="w-16 h-16 bg-gold rounded-full flex items-center justify-center mx-auto mb-4">
                                        <span className="text-2xl">✨</span>
                                    </div>
                                    <h4 className={cn(
                                        "text-lg font-bold text-navy dark:text-white mb-2",
                                        isRTL && "font-arabic"
                                    )}>{t('permanence.visual.after.label')}</h4>
                                    <p className={cn(
                                        "text-slate-600 dark:text-slate-300 text-sm",
                                        isRTL && "font-arabic"
                                    )}>
                                        {t('permanence.visual.after.description')}
                                    </p>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
} 
